import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import { AtSign, Hash, History, Maximize2, Minus, PanelRight, PictureInPicture2, Slash, Sparkles, X } from "lucide-react";
import { useRuntime } from "../../react/runtime/RuntimeContext";
import { useSession } from "../../react/providers/SessionProvider";
import { useAssistantPanel, MIN_WIDTH, MAX_WIDTH, type AssistantMode } from "../../react/providers/AssistantPanelProvider";
import { useAssistantBinding } from "../../react/providers/AssistantBindingProvider";
import { useCommand } from "../../react/hooks/useCommand";
import { command as makeCommand } from "../../core";
import { AssistantApiError } from "../../core/services/AssistantService";
import { AssistantComposer } from "./AssistantComposer";
import { AssistantHistoryList } from "./AssistantHistoryPopover";
import { AssistantMessageLine } from "./AssistantMessageLine";
import { AssistantNavigationGuard } from "./AssistantNavigationGuard";
import { buildGreeting } from "./greetings";
import type { SlashCommand } from "./SlashPalette";
import { useQueryClient } from "@tanstack/react-query";
import type {
    ActionButton,
    AssistantStreamEvent,
    ChatLine,
    CustomFieldProposal,
    SendMessageRequest,
    SessionMode,
    StartSessionRequest,
    StartSessionResponse,
} from "./types";

// Slice 1 — minimal panel with session lifecycle + SSE streaming.
//
//   - When closed: renders an empty fragment (sibling layout collapses
//     it to zero width via CSS — `.assistant-panel--closed` is hidden).
//   - When open: header (title + close) + scrollable message body +
//     composer at the bottom.
//   - Session starts lazily on the first user message (no early
//     network if the user opens the panel and never types). Closing
//     the panel does NOT close the session — that lands when we add
//     history (Slice 3); for now the session lives until tab close.
//   - Streaming via runtime.services.assistant.sendMessageStream with
//     an AbortController per turn so we can cancel mid-stream on
//     panel close.
//   - Slice 1 runs ONLY in global mode (no AssistantBindingProvider
//     wired yet); Slice 2 publishes the binding and switches to
//     contextual when the user is inside an action.

export function AssistantPanel() {
    const runtime = useRuntime();
    const session = useSession();
    const panel = useAssistantPanel();
    const binding = useAssistantBinding();
    const dispatch = useCommand();
    const queryClient = useQueryClient();

    const [sessionId, setSessionId] = useState<string | null>(null);
    // Mode is derived from the binding: a published model key → contextual,
    // otherwise global. Locks at session start so a mid-conversation
    // navigation doesn't suddenly switch the mode under the assistant.
    const [mode, setMode] = useState<SessionMode | null>(null);
    const [lines, setLines] = useState<ChatLine[]>([]);
    const [isStreaming, setIsStreaming] = useState(false);
    // In-panel conversation history view (Enh 05) — replaces the header popover.
    const [historyOpen, setHistoryOpen] = useState(false);

    const abortRef = useRef<AbortController | null>(null);
    const bodyRef = useRef<HTMLDivElement | null>(null);

    const greeting = useMemo(
        () => buildGreeting(session.userId, binding),
        [session.userId, binding],
    );

    // Cancel in-flight turn on unmount or close.
    useEffect(() => {
        return () => {
            abortRef.current?.abort();
        };
    }, []);

    // Auto-scroll body on new line / streaming delta.
    useEffect(() => {
        const el = bodyRef.current;
        if (!el) return;
        el.scrollTop = el.scrollHeight;
    }, [lines, isStreaming]);

    // The send loop — starts a session on first turn, then streams.
    const send = useCallback(
        async (body: string) => {
            setLines((prev) => [...prev, { role: "user", body }]);

            // Reserve the assistant slot up front so deltas flow in-place.
            // ChatLine.assistant carries opsApplied + actions; slice 1 just
            // appends streamed text.
            setLines((prev) => [...prev, { role: "assistant", body: "", opsApplied: 0, actions: [] }]);
            setIsStreaming(true);

            try {
                // Lazy session start on first turn. Mode is locked here from
                // the binding's current state — contextual when an action
                // is mounted, global otherwise. Mid-conversation nav doesn't
                // re-key the session.
                let sid = sessionId;
                let activeMode = mode;
                if (!sid) {
                    activeMode = binding.anchoredModelKey ? "contextual" : "global";
                    const startReq: StartSessionRequest = {
                        mode: activeMode,
                        anchored_action_key: binding.anchoredActionKey,
                        anchored_model_key: binding.anchoredModelKey,
                        initial_view_state: binding.viewState ?? null,
                    };
                    const startRes = (await runtime.services.assistant.startSession(
                        startReq as never,
                    )) as unknown as StartSessionResponse;
                    sid = startRes.session_id;
                    setSessionId(sid);
                    setMode(activeMode);
                }

                const controller = new AbortController();
                abortRef.current = controller;

                // Per-turn payload — send the user's CURRENT screen state so
                // the assistant grounds on what they're actually looking at,
                // not what the session was opened with.
                const sendReq: SendMessageRequest = {
                    body,
                    action_key: binding.anchoredActionKey,
                    view_type: binding.viewType,
                    current_view_state: binding.viewState,
                };
                await runtime.services.assistant.sendMessageStream(
                    sid,
                    sendReq as never,
                    (raw) => {
                        const evt = raw as unknown as AssistantStreamEvent;
                        if (evt.type === "text_delta") {
                            setLines((prev) => {
                                const next = prev.slice();
                                const last = next[next.length - 1];
                                if (last && last.role === "assistant") {
                                    next[next.length - 1] = { ...last, body: last.body + evt.text };
                                }
                                return next;
                            });
                        } else if (evt.type === "complete") {
                            // Apply view-intent ops (auto-apply when binding is
                            // present; global mode has no applyOps). Slice 3
                            // adds the user-confirmed chip path when the
                            // auto-apply preference is off.
                            const ops = evt.view_intent?.ops ?? [];
                            let opsApplied = 0;
                            if (ops.length > 0 && binding.anchoredModelKey) {
                                binding.applyOps(ops, (count) => {
                                    opsApplied = count;
                                });
                            }
                            // Best-effort provenance notification — fire and
                            // forget; failures must not block the UI.
                            if (opsApplied > 0 && sid) {
                                for (let i = 0; i < ops.length; i++) {
                                    runtime.services.assistant
                                        .notifyIntentApplied(sid, {
                                            message_id: evt.message_id,
                                            op_index: i,
                                            new_view_state: binding.viewState,
                                        } as never)
                                        .catch(() => {});
                                }
                            }
                            setLines((prev) => {
                                const next = prev.slice();
                                const last = next[next.length - 1];
                                if (last && last.role === "assistant") {
                                    next[next.length - 1] = {
                                        ...last,
                                        body: evt.body || last.body,
                                        actions: evt.actions ?? [],
                                        opsApplied,
                                        usage: evt.usage,
                                    };
                                }
                                return next;
                            });
                        } else if (evt.type === "error") {
                            // Replace the empty assistant placeholder with
                            // an error line; if the assistant already
                            // streamed some text before erroring, append a
                            // separate error line below so the partial
                            // reply isn't lost.
                            setLines((prev) => {
                                const next = prev.slice();
                                const last = next[next.length - 1];
                                if (last && last.role === "assistant" && last.body === "") {
                                    next[next.length - 1] = { role: "error", body: evt.message };
                                } else {
                                    next.push({ role: "error", body: evt.message });
                                }
                                return next;
                            });
                        }
                        // "started" is informational — nothing to do.
                    },
                    controller.signal,
                );
            } catch (err) {
                if ((err as Error)?.name === "AbortError") return;
                const msg =
                    err instanceof AssistantApiError
                        ? err.message
                        : err instanceof Error
                          ? err.message
                          : String(err);
                setLines((prev) => {
                    const next = prev.slice();
                    const last = next[next.length - 1];
                    if (last && last.role === "assistant" && last.body === "") {
                        next[next.length - 1] = { role: "error", body: msg };
                    } else {
                        next.push({ role: "error", body: msg });
                    }
                    return next;
                });
            } finally {
                setIsStreaming(false);
                abortRef.current = null;
            }
        },
        [runtime, sessionId, mode, binding],
    );

    // Action-button click — 4 kinds. navigate / open_record route through
    // existing platform commands; apply_to_current_view dispatches a
    // single view-intent op via binding.applyOps; export is deferred to
    // Slice 4 (needs ListView export bridge).
    const handleActionClick = useCallback(
        (action: ActionButton) => {
            const payload = action.payload ?? {};
            switch (action.kind) {
                case "navigate": {
                    const actionKey = (payload as Record<string, unknown>).action_key as string | undefined;
                    if (actionKey) {
                        dispatch(makeCommand("OPEN_ACTION", { actionKey }));
                    }
                    break;
                }
                case "open_record": {
                    const recordId =
                        ((payload as Record<string, unknown>).record_id as string | undefined) ??
                        ((payload as Record<string, unknown>).record_uuid as string | undefined);
                    const targetModel = (payload as Record<string, unknown>).model_key as string | undefined;
                    if (recordId) {
                        dispatch(makeCommand("OPEN_RECORD", { recordRef: recordId, modelKey: targetModel }));
                    }
                    break;
                }
                case "apply_to_current_view": {
                    // Treat the chip's payload as a single ViewIntentOp.
                    const op = payload as { op?: string; payload?: Record<string, unknown> };
                    if (op.op) {
                        binding.applyOps([{ op: op.op as never, payload: op.payload ?? {} }]);
                    }
                    break;
                }
                case "export": {
                    // Deferred — wires into deliverReport / ListView export
                    // bridge in Slice 4 polish.
                    break;
                }
                case "propose_custom_field": {
                    // Rendered as a confirm card (see AssistantMessageLine), not
                    // a thin chip — the write happens in handleCustomFieldConfirm.
                    break;
                }
            }
        },
        [dispatch, binding],
    );

    // Phase 4C — confirm the AI's custom-field proposal: two real ede.create
    // commands (RBAC-checked) then re-compose so the field renders in place.
    // Errors (incl. RBAC 403) propagate → the command-bus middleware shows the
    // destructive toast; the card re-enables its button.
    const handleCustomFieldConfirm = useCallback(
        async (p: CustomFieldProposal): Promise<void> => {
            if (!binding.viewParentId) {
                throw new Error("This view can't be customised (no DB-backed parent view).");
            }
            // 1) the property definition.
            const def = (await runtime.services.action.createRecord("ir.model.property.definition", {
                model_id: p.model_id,
                key: p.field.key,
                label: p.field.label,
                property_type: p.field.type,
                required: p.field.required,
                help: p.field.help,
                // Enh 02 — org-scope: organization scope restricts the definition
                // to the active org; global scope (empty) applies to all orgs.
                organization_ids: p.scope === "organization" && p.organization_id ? [p.organization_id] : [],
            })) as { record_uuid?: string };
            // 2) selection options, if any.
            if (p.field.type === "selection" && p.field.options && def.record_uuid) {
                let seq = 10;
                for (const opt of p.field.options) {
                    await runtime.services.action.createRecord("ir.model.property.selection", {
                        definition_id: def.record_uuid,
                        key: opt.key,
                        label: opt.label,
                        sequence: seq,
                    });
                    seq += 10;
                }
            }
            // 3) the ir.application.view extension placing the property.
            const arch =
                `<xpath expr="${p.placement.xpath}" position="${p.placement.position}">` +
                `<property name="properties:${p.field.key}"/></xpath>`;
            await runtime.services.action.createRecord("ir.application.view", {
                view_key: `ext_${p.model_key.replace(/\./g, "_")}_${p.field.key}_user`,
                view_type: binding.viewType,
                mode: "extension",
                owner: "user",
                parent_id: binding.viewParentId,
                scope: p.scope,
                organization_id: p.scope === "organization" ? p.organization_id : null,
                arch,
            });
            // 4) re-compose: the new property re-renders at its placement.
            queryClient.invalidateQueries({ queryKey: ["action", binding.anchoredActionKey] });
            queryClient.invalidateQueries({ queryKey: ["action-records"] });
            queryClient.invalidateQueries({ queryKey: ["record"] });
        },
        [runtime, queryClient, binding.viewParentId, binding.viewType, binding.anchoredActionKey],
    );

    // Slash-command handler — eight built-ins. /clear and /new reset
    // local conversation state; /history is handled by the popover
    // trigger (no-op here); /global toggles a per-turn flag (deferred);
    // /fav / /unfav / /rename act on the current session.
    const handleSlash = useCallback(
        (cmd: SlashCommand, arg?: string) => {
            (async () => {
                if (cmd.id === "clear" || cmd.id === "new") {
                    abortRef.current?.abort();
                    setLines([]);
                    if (sessionId) {
                        runtime.services.assistant.closeSession(sessionId).catch(() => {});
                    }
                    setSessionId(null);
                    setMode(null);
                    return;
                }
                if (cmd.id === "help") {
                    setLines((prev) => [
                        ...prev,
                        {
                            role: "system",
                            body: "Built-in commands: /fav · /unfav · /rename · /new · /clear · /global · /history · /help",
                        },
                    ]);
                    return;
                }
                if (!sessionId) {
                    setLines((prev) => [
                        ...prev,
                        { role: "system", body: "Start a conversation first — type a message and send." },
                    ]);
                    return;
                }
                try {
                    if (cmd.id === "fav") {
                        await runtime.services.assistant.starSession(sessionId);
                    } else if (cmd.id === "unfav") {
                        // The star endpoint is a toggle; same call.
                        await runtime.services.assistant.starSession(sessionId);
                    } else if (cmd.id === "rename" && arg) {
                        await runtime.services.assistant.renameSession(sessionId, arg);
                    } else if (cmd.id === "global") {
                        setLines((prev) => [
                            ...prev,
                            {
                                role: "system",
                                body: "Next turn will route through the global tool surface. (Per-turn /global toggle is read by the backend; client UI hint only.)",
                            },
                        ]);
                    }
                } catch (err) {
                    const msg = err instanceof Error ? err.message : String(err);
                    setLines((prev) => [...prev, { role: "error", body: msg }]);
                }
            })();
        },
        [sessionId, runtime],
    );

    // Resume a session id from the history popover — seed the in-memory
    // state so future turns continue against the persisted conversation.
    // (Re-fetching the historical transcript is Slice 4 polish — for now
    // the panel shows an empty scrollback after resume and the next turn
    // will land in the resumed session.)
    const handleResume = useCallback(
        (id: string) => {
            abortRef.current?.abort();
            setLines([]);
            setSessionId(id);
            setMode(null);  // mode locks on next turn's startSession path
        },
        [],
    );

    // Resize-handle drag — sets pixel width via the provider.
    const onResizePointerDown = useCallback(
        (e: ReactPointerEvent<HTMLDivElement>) => {
            e.preventDefault();
            const startX = e.clientX;
            const startW = panel.width;
            const onMove = (ev: PointerEvent) => {
                const delta = startX - ev.clientX;
                panel.setWidth(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startW + delta)));
            };
            const onUp = () => {
                window.removeEventListener("pointermove", onMove);
                window.removeEventListener("pointerup", onUp);
            };
            window.addEventListener("pointermove", onMove);
            window.addEventListener("pointerup", onUp);
        },
        [panel],
    );

    // Nav guard — aborts the in-flight stream when the user navigates
    // mid-turn so the dangling assistant bubble doesn't keep accruing
    // deltas after the binding changes.
    //
    // MUST live above the early-return below so React's hooks-order is
    // consistent on every render (rules-of-hooks: declared hooks count
    // can't change between renders).
    const handleAbortAndClear = useCallback(() => {
        abortRef.current?.abort();
        abortRef.current = null;
        setIsStreaming(false);
        setLines((prev) => {
            const next = prev.slice();
            const last = next[next.length - 1];
            if (last && last.role === "assistant" && !last.body) {
                // Drop the empty assistant placeholder that was reserved for
                // the now-aborted turn so the scrollback doesn't carry an
                // empty bubble.
                next.pop();
            }
            return next;
        });
    }, []);

    if (!panel.open) {
        return null;
    }

    // Minimized — collapse to a launcher bubble that reopens the float box.
    if (panel.mode === "min") {
        return (
            <button
                type="button"
                className="assistant-bubble"
                onClick={() => panel.setMode("float")}
                aria-label="Open assistant"
                title="Open assistant"
                data-testid="assistant-bubble"
            >
                <Sparkles size={24} />
            </button>
        );
    }

    const isFloat = panel.mode === "float";
    const modeBtn = (m: AssistantMode | "closed", Icon: typeof Minus, label: string) => (
        <button
            type="button"
            className={`assistant-panel__modebtn${panel.mode === m ? " is-active" : ""}`}
            onClick={() => panel.setMode(m)}
            aria-label={label}
            title={label}
        >
            <Icon size={14} />
        </button>
    );

    return (
        <>
            {panel.mode === "drawer" ? (
                <div
                    className="assistant-scrim"
                    onClick={() => panel.setOpen(false)}
                    aria-hidden="true"
                    data-testid="assistant-scrim"
                />
            ) : null}
            <aside
                className={`assistant-panel assistant-panel--${panel.mode}`}
                style={isFloat ? undefined : { width: `${panel.width}px` }}
                data-testid="assistant-panel"
            >
                <AssistantNavigationGuard isStreaming={isStreaming} onAbortAndClear={handleAbortAndClear} />
                {isFloat ? null : (
                    <div
                        className="assistant-panel__resize"
                        onPointerDown={onResizePointerDown}
                        role="separator"
                        aria-orientation="vertical"
                        aria-label="Resize assistant panel"
                        data-testid="assistant-panel-resize"
                    />
                )}

                <header className="assistant-panel__header">
                    <div className="assistant-panel__title">
                        <Sparkles size={14} />
                        <span>Assistant</span>
                        {binding.recordDisplayName ? (
                            <span className="assistant-panel__sub">— {binding.recordDisplayName}</span>
                        ) : null}
                    </div>
                    <div className="assistant-panel__actions">
                        <button
                            type="button"
                            className={`assistant-panel__modebtn${historyOpen ? " is-active" : ""}`}
                            onClick={() => setHistoryOpen((v) => !v)}
                            aria-label="Conversation history"
                            title="Conversation history"
                            data-testid="assistant-history-trigger"
                        >
                            <History size={14} />
                        </button>
                        <div className="assistant-panel__modes" role="group" aria-label="Assistant presentation">
                            {modeBtn("min", Minus, "Minimize to bubble")}
                            {modeBtn("float", PictureInPicture2, "Floating chat box")}
                            {modeBtn("drawer", Maximize2, "Expand to side drawer")}
                            {modeBtn("dock", PanelRight, "Dock — work and chat side by side")}
                        </div>
                        <button
                            type="button"
                            className="assistant-panel__close"
                            onClick={() => panel.setOpen(false)}
                            aria-label="Close assistant"
                            data-testid="assistant-panel-close"
                        >
                            <X size={14} />
                        </button>
                    </div>
                </header>

            {historyOpen ? (
                <AssistantHistoryList
                    onResume={(id) => {
                        handleResume(id);
                        setHistoryOpen(false);
                    }}
                    onBack={() => setHistoryOpen(false)}
                />
            ) : (
              <>
            <div
                className={`assistant-panel__body${lines.length === 0 ? " assistant-panel__body--empty" : ""}`}
                ref={bodyRef}
                data-testid="assistant-panel-body"
            >
                {lines.length === 0 ? (
                    <div className="assistant-empty" data-testid="assistant-empty">
                        <div className="assistant-empty__icon">
                            <Sparkles size={28} />
                        </div>
                        <h3 className="assistant-empty__title">Hello There!</h3>
                        <p className="assistant-empty__subtitle">{greeting}</p>
                        <ul className="assistant-empty__hints">
                            <li>
                                <span className="assistant-empty__hint-icon"><Slash size={12} /></span>
                                <span><code>/command</code> — quick actions like</span>
                            </li>
                            <li>
                                <span className="assistant-empty__hint-icon"><Hash size={12} /></span>
                                <span><code>#record</code> — record-specific action</span>
                            </li>
                            <li>
                                <span className="assistant-empty__hint-icon"><AtSign size={12} /></span>
                                <span><code>@agent</code> — invoke an agent (coming soon)</span>
                            </li>
                        </ul>
                    </div>
                ) : (
                    <>
                        {lines.map((line, i) => (
                            <AssistantMessageLine
                                key={i}
                                line={line}
                                onActionClick={handleActionClick}
                                onCustomFieldConfirm={handleCustomFieldConfirm}
                            />
                        ))}
                        {/* Thinking indicator lives inside the empty assistant
                            bubble itself (see AssistantMessageLine) — no need
                            for a separate dots line below. */}
                    </>
                )}
            </div>

            <footer className="assistant-panel__footer">
                <AssistantComposer onSubmit={send} onSlashCommand={handleSlash} disabled={isStreaming} />
            </footer>
              </>
            )}
            </aside>
        </>
    );
}
